import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';

const ModalLayout = ({ open, closeModal, children }) => {
    const [showConfirm, setShowConfirm] = useState(false);

    const handleRequestClose = (param) => {
        if (param === true || param?.direct === true) {
            setShowConfirm(false);
            if (closeModal) closeModal();
        } else {
            setShowConfirm(true);
        }
    };

    const handleConfirmDiscard = () => {
        setShowConfirm(false);
        if (closeModal) closeModal();
    };

    const handleCancelDiscard = () => {
        setShowConfirm(false);
    };

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog
                as="div"
                className="fixed inset-0 z-[200] overflow-y-auto"
                onClose={() => {}}
            >
                {/* Close Button */}
                <div 
                    onClick={() => handleRequestClose()} 
                    className="fixed top-5 right-5 md:top-8 md:right-8 cursor-pointer z-[210] p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors shadow-sm"
                    title="Close Modal"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </div>

                <div className="min-h-screen text-center overflow-y-auto py-8 px-4 flex items-center justify-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs transition-opacity pointer-events-auto" />
                    </Transition.Child>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <div className="relative inline-block w-full max-w-xl md:max-w-2xl my-8 text-left align-middle transition-all transform bg-white rounded-3xl p-6 sm:p-8 shadow-2xl z-40 border border-slate-100">
                            {React.isValidElement(children)
                                ? React.cloneElement(children, { 
                                    closeModal: handleRequestClose,
                                    directCloseModal: () => {
                                        setShowConfirm(false);
                                        if (closeModal) closeModal();
                                    }
                                  })
                                : children}
                        </div>
                    </Transition.Child>
                </div>

                {/* Custom Confirmation Popup Dialog */}
                {showConfirm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs animate-fadeIn">
                        <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-sm w-full shadow-2xl border border-slate-100 text-center animate-scaleUp z-50">
                            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200/80 flex items-center justify-center mx-auto mb-4 shadow-2xs">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            
                            <h3 className="text-base font-bold text-slate-900 mb-1">
                                Discard Unsaved Changes?
                            </h3>
                            <p className="text-xs text-slate-500 font-medium leading-relaxed mb-6">
                                Are you sure you want to cancel? Any unsaved project details will be lost.
                            </p>

                            <div className="flex items-center justify-center gap-3">
                                <button
                                    onClick={handleCancelDiscard}
                                    className="flex-1 py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all"
                                >
                                    Keep Editing
                                </button>
                                <button
                                    onClick={handleConfirmDiscard}
                                    className="flex-1 py-2.5 px-4 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl transition-all shadow-2xs"
                                >
                                    Discard & Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </Transition>
    );
};

export default ModalLayout;
